import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';
import { getContinueShoppingData } from '@client/redux/modules/continueShopping';

import { ContinueShoppingDataType } from '../types';

export interface UseContinueShoppingReturn {
    shouldShowContinueShopping: boolean;
    continueShoppingData: ContinueShoppingDataType;
}

/**
 * Custom hook for managing Continue Shopping functionality
 *
 * @returns {UseContinueShoppingReturn} Object containing:
 * - shouldShowContinueShopping: Whether to show the Continue Shopping carousel on the All tab
 * - continueShoppingData: Data for the carousel (categories with products)
 */

const shouldDisplayCarousel = (
    tabId: string | null,
    continueShoppingDataLength: number,
): boolean => {
    return tabId === CategoryTabIds.ALL && continueShoppingDataLength > 0;
};

export const useContinueShopping = (): UseContinueShoppingReturn => {
    const categoryTabId = useSelector(getCategoryTabId);
    const continueShoppingData = useSelector(getContinueShoppingData);

    const shouldShowContinueShopping = useMemo(() => {
        return shouldDisplayCarousel(categoryTabId, continueShoppingData?.length);
    }, [categoryTabId, continueShoppingData?.length]);

    return useMemo(
        () => ({
            shouldShowContinueShopping,
            continueShoppingData: continueShoppingData as ContinueShoppingDataType,
        }),
        [shouldShowContinueShopping, continueShoppingData],
    );
};
